import { useAiChatActions } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { HttpError, isNotFoundHttpError } from '@euler/lib/request/HttpError';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiDraftDetail } from '@euler/model/bi/draft';
import { useServiceFactory } from '@euler/services/factory';
import { useLocalObservable } from '@euler/utils/hooks/useLocalObservable';
import {
  catchError,
  filter,
  from,
  map,
  scan,
  startWith,
  switchMap,
} from 'rxjs';

export type BiDraftState =
  | { kind: 'loading' }
  | { kind: 'ready'; draft: BiDraftDetail }
  /** Past its 30 days; `message` is the server's catalogued sentence. */
  | { kind: 'expired'; message: string }
  /** Gone, or another member's draft. */
  | { kind: 'missing' }
  | { kind: 'failed' };

const kExpiredFallback = '这张图表已过期，请重新提问生成。';

function failureState(error: unknown): BiDraftState {
  if (error instanceof HttpError && error.subCode === 'bi_draft_expired')
    return { kind: 'expired', message: error.message || kExpiredFallback };
  return isNotFoundHttpError(error) ? { kind: 'missing' } : { kind: 'failed' };
}

/**
 * The draft a `bi_visualization` fence names. Read once, and again after
 * an add tap for it settles, so the card shows where it was added; a
 * failed read after the first keeps the chart on screen.
 */
export function useBiDraft(draftId: string): BiDraftState {
  const { biService } = useServiceFactory();
  const { tapSettled$ } = useAiChatActions();
  return useLocalObservable<BiDraftState>(
    () =>
      tapSettled$.pipe(
        filter(
          tap =>
            tap.kind === 'bi' &&
            tap.answer.action === BiAnswerAction.Add &&
            tap.answer.draftIds.includes(draftId),
        ),
        startWith(null),
        switchMap(() =>
          from(biService.draft(draftId)).pipe(
            map((draft): BiDraftState => ({ kind: 'ready', draft })),
            catchError((error): [BiDraftState] => {
              console.error('BI draft failed', draftId, error);
              return [failureState(error)];
            }),
          ),
        ),
        scan((current: BiDraftState, next: BiDraftState) =>
          current.kind === 'ready' && next.kind !== 'ready' ? current : next,
        ),
      ),
    { kind: 'loading' },
    [biService, draftId, tapSettled$],
  );
}
